import React, { ReactNode, useCallback, useContext, useMemo } from "react";
import styled from "styled-components";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { Steps } from "@shared/features/bots/BotForm/common/types.ts";
import PageTabs, { TabItem } from "common/Tabs/PageTabs.tsx";
import { newLookFormScope } from "features/bots/BotForm/NewLook/formScope";
import { BOTTOM_SUMMARY_CLASS } from "common/constants.ts";
import { AmountWrapper } from "features/bots/BotForm/General/AmountSelector";
import {
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PHONE_HIT,
  NEW_LOOK_PHONE_ROW_PAD,
  NEW_LOOK_PHONE_SECTION_GAP,
} from "common/NewLook/layout";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import { FormFieldTitle } from "features/bots/common/styles";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { useFixedBarBottomInset } from "features/onboarding/common/PhoneActionBar";
import { BOT_FORM_PHONE_ACTION_ROW_ID } from "testIds";

interface NewLookMobileBotFormProps {
  valuesModifiedMessage: ReactNode;
  copiedToWorkspaceMessage: ReactNode;
  tabsItems: {
    key: Steps;
    label: string | React.JSX.Element;
  }[];
  tabToComponent: Record<Steps, React.JSX.Element>;
  /** The action row (the summary's phone footer). */
  summary: ReactNode;
  /** Basic / advanced switch, over the step tabs (grid and signals bots). */
  advancedSwitch?: ReactNode;
  /** Notices for the whole form (group configuration), atop General. */
  notice?: ReactNode;
}

/**
 * New look (phone): swipeable step tabs, the fields with the phone gutter and
 * the actions as one compact row. The row follows the fields of the shown
 * step and sticks to the bottom of the screen once they scroll past it, so a
 * short step leaves no empty band between the fields and the actions.
 */
const NewLookMobileBotForm = ({
  valuesModifiedMessage,
  copiedToWorkspaceMessage,
  tabsItems: propsTabsItems,
  tabToComponent,
  summary,
  advancedSwitch,
  notice,
}: NewLookMobileBotFormProps) => {
  const { setTab, isAdvancedView, tab } = useContext(BotFormContext)!;
  // Onboarding: the row stays on the bottom edge even under a short step,
  // outside the tabs.
  const pinActions = !!useContext(OnboardingBotSetupContext);
  const bottomInset = useFixedBarBottomInset();

  const tabsItems = useMemo(() => {
    const hasTab = propsTabsItems.some((tabItem) => tabItem.key === tab);
    return propsTabsItems.map((tabItem, index) => {
      // The row goes with the step that is shown (rendered once, so its
      // test ids stay unique); the tabs fall back to the first step.
      const isShown = hasTab ? tabItem.key === tab : index === 0;
      return {
        ...tabItem,
        children: (
          <TabWrapper>
            <Fields>
              {copiedToWorkspaceMessage}
              {valuesModifiedMessage}
              {index === 0 && notice && <Notices>{notice}</Notices>}
              {tabToComponent[tabItem.key]}
            </Fields>
            {isShown && !pinActions && (
              <ActionBar
                className={BOTTOM_SUMMARY_CLASS}
                data-testid={BOT_FORM_PHONE_ACTION_ROW_ID}
                $bottomInset={bottomInset}
              >
                {summary}
              </ActionBar>
            )}
          </TabWrapper>
        ),
        forceRender: true,
      };
    });
  }, [
    propsTabsItems,
    valuesModifiedMessage,
    copiedToWorkspaceMessage,
    tabToComponent,
    notice,
    summary,
    tab,
    pinActions,
  ]);

  const onPageTabsChange = useCallback(
    (tabItem: TabItem) => {
      setTab(tabItem.key as Steps);
    },
    [setTab]
  );

  return (
    <Wrapper>
      {advancedSwitch && <SwitchRow>{advancedSwitch}</SwitchRow>}
      <ContentWrapper>
        <PageTabs
          items={tabsItems}
          onTabChange={onPageTabsChange}
          hideTabs={!isAdvancedView}
          defaultKey={tab}
        />
      </ContentWrapper>
      {/* Onboarding: one row under the tabs, so it stays put as they swipe. */}
      {pinActions && (
        <ActionBar
          className={BOTTOM_SUMMARY_CLASS}
          data-testid={BOT_FORM_PHONE_ACTION_ROW_ID}
          $bottomInset={bottomInset}
        >
          {summary}
        </ActionBar>
      )}
    </Wrapper>
  );
};

export default NewLookMobileBotForm;

/** Phone: the height of the form's inputs (selects are 40 already). */
const PHONE_INPUT_HEIGHT = NEW_LOOK_PHONE_HIT;

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  ${newLookFormScope};
  /* Phone: field labels in the phone label role (12.5 / 400). */
  ${FormFieldTitle} {
    ${newLookPhoneType.label};
  }

  /* One input height: the slider inputs (32), the amount's input + select
     group (34) and the affixed inputs match the 40px text inputs. */
  .ant-input-affix-wrapper,
  input.ant-input:not(.ant-input-affix-wrapper input),
  .ant-input-number:not(.ant-input-affix-wrapper .ant-input-number) {
    min-height: ${PHONE_INPUT_HEIGHT}px;
  }
  .ant-input-affix-wrapper {
    height: auto !important;
  }
  input.ant-input:not(.ant-input-affix-wrapper input) {
    height: ${PHONE_INPUT_HEIGHT}px !important;
  }
  ${AmountWrapper} {
    min-height: ${PHONE_INPUT_HEIGHT}px;
  }

  /* Option cards (radio groups) on the control radius. */
  .ant-radio-group .ant-radio-wrapper {
    border-radius: ${NEW_LOOK_RADIUS.control}px;
  }
`;
const SwitchRow = styled.div`
  flex: none;
  padding: ${NEW_LOOK_PHONE_ROW_PAD}px ${NEW_LOOK_PHONE_GUTTER}px 4px;
`;
const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;
const TabWrapper = styled.div`
  overflow-y: auto;
  /* Its end doesn't pull the page along. */
  overscroll-behavior-y: contain;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const Fields = styled.div`
  flex: none;
  padding: 16px ${NEW_LOOK_PHONE_GUTTER}px
    ${NEW_LOOK_PHONE_SECTION_GAP - NEW_LOOK_PHONE_ROW_PAD}px;
`;
const Notices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  &:empty,
  > div:empty {
    display: none;
  }
`;
/* Sticks to the bottom of the step's scroller; the panel colour fades in
   over its top edge so fields scrolling under it read as passing beneath. */
const ActionBar = styled.div<{ $bottomInset: string }>`
  position: sticky;
  bottom: 0;
  z-index: 5;
  padding: ${NEW_LOOK_PHONE_ROW_PAD}px ${NEW_LOOK_PHONE_GUTTER}px
    calc(${NEW_LOOK_PHONE_ROW_PAD}px + ${(p) => p.$bottomInset});
  background: ${(p) => getNewLookTokens(p.theme).panel};
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: 16px;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      transparent,
      ${(p) => getNewLookTokens(p.theme).panel}
    );
  }
`;
